import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { conditionLabel, stateLabel, creditStatusLabel } from '../utils/usedGame';
import { matchesSearchTerms } from '../utils/searchTerms';
import { isAdmin } from '../utils/enums';

var config = process.env;

// El filtro de estado. El default esconde los vendidos (el listado es para trabajar con lo que
// sigue en el local o se retiró); los vendidos quedan a un click.
const STATE_FILTERS = [
    { value: 'unsold', label: 'No vendidos', match: (item) => item.state !== 'sold' },
    { value: 'available', label: 'Disponibles', match: (item) => item.state === 'available' },
    { value: 'sold', label: 'Vendidos', match: (item) => item.state === 'sold' },
    { value: 'withdrawn', label: 'Retirados', match: (item) => item.state === 'withdrawn' },
    { value: 'all', label: 'Todos', match: () => true },
];
const DEFAULT_STATE_FILTER = 'unsold';

function sellerName(item) {
    const { seller } = item;
    return seller ? [seller.firstName, seller.lastName].filter(Boolean).join(' ') : '';
}

// Lo que el buscador mira es lo que la fila muestra: nombre del juego, vendedor, celular y cédula.
function searchFields(item) {
    return [item.name, sellerName(item), item.seller && item.seller.phone, item.sellerCedula];
}

function money(value) {
    const n = Number(value || 0);
    return `$ ${n.toLocaleString('es-UY')}`;
}

// Quién trajo el juego: nombre y celular salen de su ficha de cliente (el backend los
// adjunta en `seller`), para poder llamarlo sin salir del listado. Sin ficha, queda la cédula.
function SellerCell({ item }) {
    const { seller } = item;
    if (!seller) {
        return (
            <td>
                {item.sellerCedula}
                <div className="small text-muted">Sin ficha de cliente</div>
            </td>
        );
    }
    const name = sellerName(item) || item.sellerCedula;
    return (
        <td>
            <div>{name}</div>
            <div className="small text-muted">CI {item.sellerCedula}</div>
            <div className="small text-muted">
                {seller.phone
                    ? <a href={`tel:${seller.phone.replace(/[^\d+]/g, '')}`}>{seller.phone}</a>
                    : 'Sin celular'}
            </div>
            <button type="button" className="btn btn-sm btn-white mt-1" onClick={() => navigateTo(`/clientDetail?cedula=${encodeURIComponent(seller.cedula)}`)}>
                Ver cliente
            </button>
        </td>
    );
}

class UsedGamesPage extends Component {
    constructor() {
        super();
        this.state = { checked: false, role: 0, items: [], loading: true, search: '', stateFilter: DEFAULT_STATE_FILTER };
    }

    async componentDidMount() {
        const role = await validateSession();
        this.setState({ checked: true, role });
        this.fetchItems();
    }

    fetchItems = () => {
        this.setState({ loading: true });
        const request = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'access-token': localStorage.getItem('token'),
            },
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'used-games', request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    if (data.error === 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                    this.setState({ loading: false });
                } else {
                    this.setState({ items: data.data || [], loading: false });
                }
            });
    }

    withdraw = (code) => {
        if (!window.confirm('¿Retirar este usado? Se cancela el crédito y deja de mostrarse en la tienda.')) {
            return;
        }
        const request = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'access-token': localStorage.getItem('token'),
            },
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `used-games/${code}/withdraw`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                } else {
                    this.fetchItems();
                }
            });
    }

    visibleItems() {
        const filter = STATE_FILTERS.find((f) => f.value === this.state.stateFilter) || STATE_FILTERS[0];
        return this.state.items
            .filter(filter.match)
            .filter((item) => matchesSearchTerms(searchFields(item), this.state.search));
    }

    renderToolbar(visibles) {
        const { items, search, stateFilter } = this.state;
        return (
            <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
                <input
                    type="search"
                    className="form-control"
                    style={{ maxWidth: 320 }}
                    placeholder="Buscar por juego, vendedor, celular o cédula"
                    value={search}
                    onChange={(e) => this.setState({ search: e.target.value })}
                />
                <select
                    className="custom-select"
                    style={{ maxWidth: 220 }}
                    value={stateFilter}
                    onChange={(e) => this.setState({ stateFilter: e.target.value })}
                    aria-label="Estado"
                >
                    {STATE_FILTERS.map((f) => (
                        <option key={f.value} value={f.value}>
                            {f.label} ({items.filter(f.match).length})
                        </option>
                    ))}
                </select>
                {!this.state.loading && (
                    <span className="text-muted small">{visibles.length} de {items.length}</span>
                )}
            </div>
        );
    }

    renderActions(item) {
        return (
            <td className="text-right text-nowrap">
                {item.state === 'sold' && item.soldPurchaseId != null && (
                    <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo(`/purchase?id=${item.soldPurchaseId}`)}>
                        Ver venta
                    </button>
                )}
                {/* Vendido o retirado es final: no hay nada que editar (el backend también lo rechaza). */}
                {item.state === 'available' && (
                    <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo(`/editUsedGame?code=${item.code}`)}>
                        Editar
                    </button>
                )}
                {item.state === 'available' && (
                    <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={() => this.withdraw(item.code)}>
                        Retirar
                    </button>
                )}
            </td>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const visibles = this.visibleItems();
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={22} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <HeaderComponent title="Usados" subtitle="Juegos usados" />
                                <div>
                                    {isAdmin(this.state.role) && (
                                        <button type="button" className="btn btn-white mr-2" onClick={() => navigateTo('/usedGamesAnalytics')}>
                                            <i className="material-icons mr-1">insert_chart_outlined</i>Estadísticas
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-accent" onClick={() => navigateTo('/addUsedGame')}>
                                        + Nuevo usado
                                    </button>
                                </div>
                            </div>
                            {this.renderToolbar(visibles)}
                            <div className="card card-small mb-4">
                                <div className="card-body p-0">
                                    <table className="table mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Condición</th>
                                                <th>Precio</th>
                                                <th>Vendedor</th>
                                                <th>Estado</th>
                                                <th>Crédito</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.state.loading && (
                                                <tr><td colSpan="7" className="text-center">Cargando…</td></tr>
                                            )}
                                            {!this.state.loading && this.state.items.length === 0 && (
                                                <tr><td colSpan="7" className="text-center text-muted">No hay juegos usados todavía.</td></tr>
                                            )}
                                            {!this.state.loading && this.state.items.length > 0 && visibles.length === 0 && (
                                                <tr><td colSpan="7" className="text-center text-muted">Ningún usado coincide con la búsqueda y el filtro.</td></tr>
                                            )}
                                            {visibles.map((item) => (
                                                <tr key={item.code}>
                                                    <td>{item.name}</td>
                                                    <td>{conditionLabel(item.condition)}</td>
                                                    <td>{money(item.price)}</td>
                                                    <SellerCell item={item} />
                                                    <td>{stateLabel(item.state)}</td>
                                                    <td>{creditStatusLabel(item.creditStatus)} ({money(item.agreedCredit)})</td>
                                                    {this.renderActions(item)}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default UsedGamesPage;
